// Icons
import {
  mdiAccountCircleOutline,
  mdiAlertCircleOutline,
  mdiArrowCollapseVertical,
  mdiAspectRatio,
  mdiAtom,
  mdiBookOpenBlankVariant,
  mdiBookOutline,
  mdiCalendarOutline,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarkedOutline,
  mdiCheck,
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronUp,
  mdiClockOutline,
  mdiClose,
  mdiCodeBraces,
  mdiCodeJson,
  mdiCodeTags,
  mdiCog,
  mdiCloudCheckOutline,
  mdiCloudSyncOutline,
  mdiContentCopy,
  mdiContentSaveOutline,
  mdiDeleteOutline,
  mdiEarth,
  mdiEyeOffOutline,
  mdiGithub,
  mdiCursorDefaultClickOutline,
  mdiDatabaseOutline,
  mdiDockWindow,
  mdiDotsHorizontal,
  mdiDownloadOutline,
  mdiDragHorizontal,
  mdiDragVertical,
  mdiEye,
  mdiEyeOutline,
  mdiFileOutline,
  mdiFilePlusOutline,
  mdiFileTreeOutline,
  mdiFilterOutline,
  mdiFlashOutline,
  mdiFolderOpenOutline,
  mdiFolderOutline,
  mdiFolderPlusOutline,
  mdiFormTextbox,
  mdiFullscreen,
  mdiFullscreenExit,
  mdiGestureTapButton,
  mdiImageOutline,
  mdiInformationOutline,
  mdiKeyboardOutline,
  mdiLanguageCss3,
  mdiLanguageJavascript,
  mdiLanguageTypescript,
  mdiLayersOutline,
  mdiLinkVariant,
  mdiLock,
  mdiLockOpenVariantOutline,
  mdiLogout,
  mdiMagnify,
  mdiMenu,
  mdiMenuDown,
  mdiMessageOutline,
  mdiNumeric,
  mdiPageLast,
  mdiPaletteOutline,
  mdiPencilOutline,
  mdiPin,
  mdiPinOutline,
  mdiPlus,
  mdiRadioboxMarked,
  mdiRayStartArrow,
  mdiRefresh,
  mdiResize,
  mdiResponsive,
  mdiShieldAccountOutline,
  mdiSlashForward,
  mdiSort,
  mdiSourceFork,
  mdiStar,
  mdiStarOutline,
  mdiTable,
  mdiTab,
  mdiTagMultiple,
  mdiTimerOutline,
  mdiToggleSwitchOutline,
  mdiTooltipOutline,
  mdiTranslate,
  mdiTuneVariant,
  mdiViewCarouselOutline,
  mdiViewColumnOutline,
  mdiViewDashboardOutline,
  mdiViewListOutline,
  mdiViewSplitHorizontal,
  mdiViewSplitVertical,
  mdiVuejs,
  mdiWaterOutline,
  mdiWeatherNight,
  mdiWeatherSunny,
  mdiWrap,
} from '@mdi/js'

// Framework
import { createPlugin, createTokensContext } from '@vuetify/v0'

// Data
import { FILE_TYPE_ICONS } from '@/data/file-types'

// Types
import type { App } from 'vue'

export const [useIconContext, provideIconContext, context] = createTokensContext({
  namespace: 'v0:icons',
  tokens: {
    'alert': mdiAlertCircleOutline,
    'left': mdiChevronLeft,
    'book-open': mdiBookOpenBlankVariant,
    'book-closed': mdiBookOutline,
    'check': mdiCheck,
    'folder': mdiFolderOutline,
    'folder-open': mdiFolderOpenOutline,
    'folder-plus': mdiFolderPlusOutline,
    'file': mdiFileOutline,
    'file-plus': mdiFilePlusOutline,
    'cog': mdiCog,
    'close': mdiClose,
    'editor': mdiCodeTags,
    'eye': mdiEye,
    'info': mdiInformationOutline,
    'layout-horizontal': mdiViewSplitVertical,
    'layout-vertical': mdiViewSplitHorizontal,
    'menu': mdiMenu,
    'chevron-up': mdiChevronUp,
    'chevron-down': mdiChevronDown,
    'chevron-right': mdiChevronRight,
    'drag-horizontal': mdiDragHorizontal,
    'drag-vertical': mdiDragVertical,
    'lang-ts': mdiLanguageTypescript,
    'lang-tsx': mdiLanguageTypescript,
    'lang-js': mdiLanguageJavascript,
    'lang-jsx': mdiLanguageJavascript,
    'lang-css': mdiLanguageCss3,
    'lang-vue': mdiVuejs,
    'lang-json': mdiCodeJson,
    ...FILE_TYPE_ICONS,
    'reset': mdiRefresh,
    'keyboard': mdiKeyboardOutline,
    'format': mdiCodeBraces,
    'fullscreen': mdiFullscreen,
    'fullscreen-exit': mdiFullscreenExit,
    'theme-light': mdiWeatherSunny,
    'theme-dark': mdiWeatherNight,
    'layers': mdiLayersOutline,
    'download': mdiDownloadOutline,
    'copy': mdiContentCopy,
    'save': mdiContentSaveOutline,
    // Linked One playground (auto-save) — not a "create" affordance
    'cloud-check': mdiCloudCheckOutline,
    'cloud-sync': mdiCloudSyncOutline,
    'account': mdiAccountCircleOutline,
    'star': mdiStar,
    'star-outline': mdiStarOutline,
    'pin': mdiPin,
    'pin-outline': mdiPinOutline,
    'plus': mdiPlus,
    'pencil': mdiPencilOutline,
    'lock': mdiLock,
    'lock-open': mdiLockOpenVariantOutline,
    'logout': mdiLogout,
    'visibility-public': mdiEarth,
    'visibility-private': mdiEyeOffOutline,
    'delete': mdiDeleteOutline,
    'fork': mdiSourceFork,
    // Brand marks for Vuetify One sign-in (paths match apps/docs)
    'github': mdiGithub,
    'google': 'M21.35 11.1H12.18V13.83H18.69C18.36 17.64 15.19 19.27 12.19 19.27C8.36 19.27 5 16.25 5 12C5 7.9 8.2 4.73 12.2 4.73C15.29 4.73 17.1 6.7 17.1 6.7L19 4.72C19 4.72 16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12C2.03 17.05 6.16 22 12.25 22C17.6 22 21.5 18.33 21.5 12.91C21.5 11.76 21.35 11.1 21.35 11.1Z',
    'discord': 'M22,24L16.75,19L17.38,21H4.5A2.5,2.5 0 0,1 2,18.5V3.5A2.5,2.5 0 0,1 4.5,1H19.5A2.5,2.5 0 0,1 22,3.5V24M12,6.8C9.32,6.8 7.44,7.95 7.44,7.95C8.47,7.03 10.27,6.5 10.27,6.5L10.1,6.33C8.41,6.36 6.88,7.53 6.88,7.53C5.16,11.12 5.27,14.22 5.27,14.22C6.67,16.03 8.75,15.9 8.75,15.9L9.46,15C8.21,14.73 7.42,13.62 7.42,13.62C7.42,13.62 9.3,14.9 12,14.9C14.7,14.9 16.58,13.62 16.58,13.62C16.58,13.62 15.79,14.73 14.54,15L15.25,15.9C15.25,15.9 17.33,16.03 18.73,14.22C18.73,14.22 18.84,11.12 17.12,7.53C17.12,7.53 15.59,6.36 13.9,6.33L13.73,6.5C13.73,6.5 15.53,7.03 16.56,7.95C16.56,7.95 14.68,6.8 12,6.8M9.93,10.59C10.58,10.59 11.11,11.16 11.1,11.86C11.1,12.55 10.58,13.13 9.93,13.13C9.29,13.13 8.77,12.55 8.77,11.86C8.77,11.16 9.28,10.59 9.93,10.59M14.1,10.59C14.75,10.59 15.27,11.16 15.27,11.86C15.27,12.55 14.75,13.13 14.1,13.13C13.46,13.13 12.94,12.55 12.94,11.86C12.94,11.16 13.45,10.59 14.1,10.59Z',
    'opencollective': 'M12 2C6.48 2 2 6.48 2 12C2 14.85 3.18 17.41 5.08 19.21L7.89 16.4C6.72 15.27 6 13.72 6 12C6 8.69 8.69 6 12 6C15.31 6 18 8.69 18 12C18 13.72 17.28 15.27 16.11 16.4L18.92 19.21C20.82 17.41 22 14.85 22 12C22 6.48 17.52 2 12 2Z',
    'link': mdiLinkVariant,
    'search': mdiMagnify,
    'tags': mdiTagMultiple,
    'wrap': mdiWrap,

    // Open-gallery feature icons (see data/feature-icons.ts)
    'feat-alert': mdiAlertCircleOutline,
    'feat-array': mdiCodeBraces,
    'feat-aspect': mdiAspectRatio,
    'feat-atom': mdiAtom,
    'feat-avatar': mdiAccountCircleOutline,
    'feat-breadcrumbs': mdiSlashForward,
    'feat-breakpoints': mdiResponsive,
    'feat-button': mdiGestureTapButton,
    'feat-carousel': mdiViewCarouselOutline,
    'feat-checkbox': mdiCheckboxMarkedOutline,
    'feat-click-outside': mdiCursorDefaultClickOutline,
    'feat-collapse': mdiArrowCollapseVertical,
    'feat-combobox': mdiMenuDown,
    'feat-context': mdiLayersOutline,
    'feat-date': mdiCalendarOutline,
    'feat-default': mdiCodeTags,
    'feat-delay': mdiTimerOutline,
    'feat-dialog': mdiDockWindow,
    'feat-drag': mdiDragVertical,
    'feat-element': mdiCodeTags,
    'feat-event': mdiFlashOutline,
    'feat-features': mdiTagMultiple,
    'feat-filter': mdiFilterOutline,
    'feat-focus': mdiRayStartArrow,
    'feat-form': mdiFormTextbox,
    'feat-group': mdiViewDashboardOutline,
    'feat-highlight': mdiMagnify,
    'feat-hotkey': mdiKeyboardOutline,
    'feat-hydration': mdiWaterOutline,
    'feat-image': mdiImageOutline,
    'feat-input': mdiFormTextbox,
    'feat-intersection': mdiEyeOutline,
    'feat-kanban': mdiViewColumnOutline,
    'feat-lazy': mdiClockOutline,
    'feat-locale': mdiTranslate,
    'feat-logger': mdiMessageOutline,
    'feat-media': mdiResponsive,
    'feat-model': mdiLinkVariant,
    'feat-motion': mdiFlashOutline,
    'feat-mutation': mdiEyeOutline,
    'feat-number': mdiNumeric,
    'feat-otp': mdiNumeric,
    'feat-overflow': mdiDotsHorizontal,
    'feat-pagination': mdiPageLast,
    'feat-permissions': mdiShieldAccountOutline,
    'feat-plugin': mdiCog,
    'feat-popover': mdiTooltipOutline,
    'feat-portal': mdiDockWindow,
    'feat-presence': mdiEyeOutline,
    'feat-progress': mdiTimerOutline,
    'feat-queue': mdiViewListOutline,
    'feat-radio': mdiRadioboxMarked,
    'feat-raf': mdiTimerOutline,
    'feat-rating': mdiStarOutline,
    'feat-reactive': mdiFlashOutline,
    'feat-registry': mdiDatabaseOutline,
    'feat-resize': mdiResize,
    'feat-rtl': mdiTranslate,
    'feat-scope': mdiLayersOutline,
    'feat-scrim': mdiCheckboxBlankOutline,
    'feat-select': mdiMenuDown,
    'feat-selection': mdiCheckboxMarkedOutline,
    'feat-single': mdiRadioboxMarked,
    'feat-slider': mdiTuneVariant,
    'feat-snackbar': mdiMessageOutline,
    'feat-sort': mdiSort,
    'feat-splitter': mdiViewSplitVertical,
    'feat-stack': mdiLayersOutline,
    'feat-step': mdiPageLast,
    'feat-storage': mdiDatabaseOutline,
    'feat-switch': mdiToggleSwitchOutline,
    'feat-system': mdiCog,
    'feat-table': mdiTable,
    'feat-tabs': mdiTab,
    'feat-theme': mdiPaletteOutline,
    'feat-timeline': mdiClockOutline,
    'feat-timer': mdiTimerOutline,
    'feat-tokens': mdiTagMultiple,
    'feat-toggle': mdiToggleSwitchOutline,
    'feat-tooltip': mdiTooltipOutline,
    'feat-tree': mdiFileTreeOutline,
    'feat-trinity': mdiAtom,
    'feat-validation': mdiShieldAccountOutline,
    'feat-virtual': mdiViewListOutline,
    'vue-router': 'M24,1.61H14.06L12,5.16,9.94,1.61H0L12,22.39ZM12,14.08,5.16,2.23H9.59L12,6.41l2.41-4.18h4.43Z',
    'pinia': 'M13.277.002c-.079-.01-.193.024-.31.09a2.346 2.346 0 0 0-.415.311c-.301.274-.65.68-.985 1.176-.672.99-1.31 2.356-1.485 3.785-.12.977-.154 1.849-.074 2.578a5.878 5.878 0 0 1 1.956-.338c.922 0 1.804.218 2.618.613.197-.637.34-1.395.445-2.246.175-1.429-.1-2.907-.499-4.028a6.695 6.695 0 0 0-.653-1.378 2.296 2.296 0 0 0-.321-.401c-.097-.092-.199-.153-.277-.162zm-6.81 2.965a.603.603 0 0 0-.288.117l-.013.011a.863.863 0 0 0-.177.327c-.051.15-.094.344-.126.565a8.205 8.205 0 0 0-.044 1.637c.08 1.222.397 2.665 1.079 3.769.093.15.193.296.294.442.726-.776 1.567-1.385 2.488-1.768-.1-.79-.06-1.714.066-2.744.038-.31.1-.616.176-.916a6.45 6.45 0 0 0-1.692-1.08 4.575 4.575 0 0 0-1.29-.366 1.805 1.805 0 0 0-.474.006zm11.751 1.789c-.21.003-.442.022-.687.06a5.974 5.974 0 0 0-2.132.76c-.009.146-.019.291-.036.437-.11.888-.258 1.684-.475 2.364.848.468 1.614 1.13 2.267 1.94.823-.85 1.43-2.05 1.787-3.13.187-.57.304-1.1.35-1.514.022-.207.027-.38.017-.51-.01-.133-.042-.206-.04-.202a.35.35 0 0 0-.114-.072 1.516 1.516 0 0 0-.375-.1 3.42 3.42 0 0 0-.562-.033zm-6.254 3.139c-2.014 0-3.83 1.087-5.147 2.834-1.316 1.748-2.131 4.16-2.131 6.807 0 2.65.806 4.258 2.131 5.218C8.132 23.707 9.96 24 11.964 24c2.004 0 3.831-.293 5.147-1.246 1.326-.96 2.133-2.568 2.133-5.218 0-2.647-.816-5.059-2.133-6.806-1.317-1.748-3.134-2.835-5.147-2.835Zm-3.297 5.209a1.282 1.282 0 0 1 .292 2.518c.115.094.177.201.164.307-.032.265-.521.424-1.09.354-.571-.07-1.007-.342-.974-.607.024-.198.302-.336.678-.364a1.282 1.282 0 0 1 .93-2.208zm6.284.22a1.282 1.282 0 0 1 .726 2.303c.437.058.757.244.757.466 0 .267-.466.483-1.04.483-.574 0-1.04-.216-1.04-.483 0-.098.063-.189.17-.265a1.282 1.282 0 0 1 .427-2.505zm-6.318.215a.822.822 0 1 0 .028 1.645.822.822 0 0 0-.028-1.645zm6.284.22a.822.822 0 1 0 .027 1.644.822.822 0 0 0-.027-1.644zm-6.543.109a.338.338 0 1 1-.023.677.338.338 0 0 1 .023-.677zm6.284.22a.339.339 0 1 1-.018 0h.018zm-3.91 1.179c.17.252.432.404.808.447.372.043.694-.05.976-.28a.145.145 0 1 1 .183.226c-.344.28-.745.394-1.192.343-.445-.051-.79-.24-1.016-.574a.145.145 0 1 1 .24-.162z',
    'vuetify-0': [
      'M18 13.5192L13.0623 22L2 3H13.6499C12.49 4.11314 11.7685 5.67266 11.7685 7.39793C11.7685 10.7765 14.5353 13.5195 17.9432 13.5195C17.9622 13.5195 17.9811 13.5194 18 13.5192V13.5192Z',
      ['M18 11C20.2091 11 22 9.20914 22 7C22 4.79086 20.2091 3 18 3C15.7909 3 14 4.79086 14 7C14 9.20914 15.7909 11 18 11Z', 0.6],
    ],
    'vuetify': [
      'M8.2241 14.2009L12 21L22 3H14.4459L8.2241 14.2009Z',
      ['M7.26303 12.4733L7.00113 12L2 3H12.5261C12.5261 3 12.5261 3 12.5261 3L7.26303 12.4733Z', 0.6],
    ],
    'tanstack': 'M11.078.042c.316-.042.65-.014.97-.014 1.181 0 2.341.184 3.472.532a12.3 12.3 0 0 1 3.973 2.086 11.9 11.9 0 0 1 3.432 4.33c1.446 3.15 1.436 6.97-.046 10.107-.958 2.029-2.495 3.727-4.356 4.965-1.518 1.01-3.293 1.629-5.1 1.848-2.298.279-4.784-.129-6.85-1.188-3.88-1.99-6.518-5.994-6.57-10.382-.01-.846.003-1.697.17-2.534.273-1.365.748-2.683 1.463-3.88a12 12 0 0 1 2.966-3.36A12.3 12.3 0 0 1 9.357.3a12 12 0 0 1 1.255-.2l.133-.016zM7.064 19.99c-.535.057-1.098.154-1.557.454.103.025.222 0 .33 0 .258 0 .52-.01.778.002.647.028 1.32.131 1.945.303.8.22 1.505.65 2.275.942.813.307 1.622.402 2.484.402.435 0 .866-.001 1.287-.12-.22-.117-.534-.095-.778-.144a11 11 0 0 1-1.556-.416 12 12 0 0 1-1.093-.467l-.23-.108a15 15 0 0 0-1.012-.44c-.905-.343-1.908-.512-2.873-.408m.808-2.274c-1.059 0-2.13.187-3.083.667q-.346.177-.659.41c-.063.046-.175.106-.199.188s.061.151.11.204c.238-.127.464-.261.718-.357 1.64-.624 3.63-.493 5.268.078.817.285 1.569.712 2.365 1.046.89.374 1.798.616 2.753.74 1.127.147 2.412.028 3.442-.48.362-.179.865-.451 1.018-.847-.189.017-.36.098-.539.154a9 9 0 0 1-.868.222c-.994.2-2.052.24-3.053.06-.943-.17-1.82-.513-2.693-.873l-.111-.046-.223-.092-.112-.046a26 26 0 0 0-1.35-.527c-.89-.31-1.842-.5-2.784-.5M9.728 1.452c-1.27.28-2.407.826-3.502 1.514-.637.4-1.245.81-1.796 1.323-.82.765-1.447 1.695-1.993 2.666-.563 1-.924 2.166-1.098 3.297-.172 1.11-.2 2.277-.004 3.388.245 1.388.712 2.691 1.448 3.897.248-.116.424-.38.629-.557.414-.359.85-.691 1.317-.978a3.5 3.5 0 0 1 .539-.264c.07-.029.187-.055.22-.132.053-.124-.045-.34-.062-.468a7 7 0 0 1-.068-1.109 9.7 9.7 0 0 1 .61-3.177c.29-.76.73-1.45 1.254-2.069.177-.21.365-.405.56-.6.115-.114.258-.212.33-.359-.376 0-.751.108-1.108.218-.769.237-1.518.588-2.155 1.084-.291.226-.504.522-.779.76-.084.073-.235.17-.352.116-.176-.083-.149-.43-.169-.59-.078-.612.154-1.387.45-1.918.473-.852 1.348-1.58 2.376-1.555.444.011.833.166 1.257.266-.107-.153-.252-.264-.389-.39a5.4 5.4 0 0 0-1.107-.8c-.163-.085-.338-.136-.509-.2-.086-.03-.195-.074-.227-.17-.06-.177.26-.342.377-.417.453-.289 1.01-.527 1.556-.54.854-.021 1.688.452 2.04 1.258.123.284.16.583.184.885l.004.057.006.085.002.029.005.057.004.056c.268-.218.457-.54.718-.774.612-.547 1.45-.79 2.245-.544a2.97 2.97 0 0 1 1.71 1.378c.097.173.365.595.171.767-.152.134-.344.03-.504-.026a3 3 0 0 0-.372-.094l-.068-.014-.069-.013a3.9 3.9 0 0 0-1.377-.002c-.282.05-.557.15-.838.192v.06c.768.006 1.51.444 1.89 1.109.157.275.235.59.295.9.075.38.022.796-.082 1.168-.035.125-.098.336-.247.365-.106.02-.195-.085-.256-.155a4.6 4.6 0 0 0-.492-.522 20 20 0 0 0-1.467-1.14c-.267-.19-.56-.44-.868-.556.087.208.171.402.2.63.088.667-.192 1.296-.612 1.798a2.6 2.6 0 0 1-.426.427c-.067.05-.151.114-.24.1-.277-.044-.31-.463-.353-.677-.144-.726-.086-1.447.114-2.158-.178.09-.307.287-.418.45a5.3 5.3 0 0 0-.612 1.138c-.61 1.617-.604 3.51.186 5.066.088.174.221.15.395.15h.157a3 3 0 0 1 .472.018c.08.01.193 0 .257.06.077.072.036.194.018.282-.05.246-.066.469-.066.72.328-.051.419-.576.535-.84.131-.298.265-.597.387-.9.06-.148.14-.314.119-.479-.024-.185-.157-.381-.25-.54-.177-.298-.378-.606-.508-.929-.104-.258-.007-.58.286-.672.161-.05.334.049.439.166.22.244.363.609.523.896l1.249 2.248q.159.286.32.57c.043.074.086.188.173.219.077.028.182-.012.26-.027.198-.04.398-.083.598-.12.24-.043.605-.035.778-.222-.253-.08-.545-.075-.808-.057-.158.01-.333.067-.479-.025-.216-.137-.36-.455-.492-.667-.326-.525-.633-1.057-.945-1.59l-.05-.084-.1-.17q-.075-.126-.149-.255c-.037-.066-.092-.153-.039-.227.056-.076.179-.08.29-.081h.021q.066.001.117-.004a10 10 0 0 1 1.347-.107c-.035-.122-.135-.26-.103-.39.071-.292.49-.383.686-.174.131.14.207.334.292.504.113.223.24.44.361.66.211.383.441.757.658 1.138l.055.094.028.047c.093.156.187.314.238.489-.753-.035-1.318-.909-1.646-1.499-.027.095.016.179.05.27q.103.282.262.54c.152.244.326.495.556.673.408.315.945.317 1.436.283.315-.022.708-.165 1.018-.068s.434.438.25.7c-.138.196-.321.27-.55.3.162.346.373.667.527 1.02.064.146.13.37.283.448.102.051.248.003.358 0-.11-.292-.317-.54-.419-.839.31.015.61.176.898.28.567.202 1.128.424 1.687.648l.258.104c.23.092.462.183.689.283.083.037.198.123.29.07.074-.043.123-.146.169-.215a10.3 10.3 0 0 0 1.393-3.208c.75-2.989.106-6.287-1.695-8.783-.692-.96-1.562-1.789-2.522-2.476-2.401-1.718-5.551-2.407-8.44-1.768m4.908 14.904c-.636.166-1.292.317-1.945.401.086.293.296.577.45.84.059.101.122.237.24.281.132.05.292-.03.417-.072-.058-.158-.155-.3-.235-.45-.033-.06-.084-.133-.056-.206.05-.137.263-.13.381-.153.31-.063.617-.142.928-.204.114-.023.274-.085.389-.047.086.03.138.1.187.174l.022.033q.043.07.097.122c.125.113.313.13.472.162-.097-.219-.259-.41-.362-.63-.06-.127-.11-.315-.242-.388-.182-.102-.557.089-.743.137m-4.01-1.457c-.03.38-.147.689-.33 1.019.21.026.423.036.629.087.154.038.296.11.449.153-.082-.224-.233-.423-.35-.63-.12-.208-.226-.462-.398-.63',
  },
})

export function createIconPlugin () {
  return createPlugin({
    namespace: 'v0:icons',
    provide: (app: App) => {
      provideIconContext(context, app)
    },
  })
}
